<template>
    <div class="ec-root">
        <!-- Top nav bar -->
        <div class="ec-topbar">
            <div class="ec-topbar-left">
                <i class="fa-duotone fa-solid fa-envelope-open-text text-primary-500 text-lg"></i>
                <span class="ec-brand-text">Email Composer</span>
            </div>
            <div class="ec-topbar-right">
                <button class="ec-topbar-btn" @click="showProfileModal = true">
                    <i class="fa-solid fa-user-pen text-gray-400 text-xs"></i>
                    <span class="ec-topbar-btn-label ec-hide-mobile">Writing as</span>
                    <span class="ec-topbar-btn-value ec-hide-mobile">{{ user.name }}</span>
                </button>
                <button class="ec-topbar-btn" @click="showHistory = true">
                    <i class="fa-solid fa-clock-rotate-left text-gray-400 text-xs"></i>
                    <span class="ec-hide-mobile">History</span>
                </button>
            </div>
        </div>

        <!-- Mobile tab bar -->
        <div class="ec-mobile-tabs">
            <button
                :class="['ec-mobile-tab', mobileTab === 'compose' && 'ec-mobile-tab-active']"
                @click="mobileTab = 'compose'"
            >
                <i class="fa-solid fa-pen-to-square text-xs"></i>
                <span>Compose</span>
            </button>
            <button
                :class="['ec-mobile-tab', mobileTab === 'preview' && 'ec-mobile-tab-active']"
                @click="mobileTab = 'preview'"
            >
                <i class="fa-solid fa-eye text-xs"></i>
                <span>Preview</span>
                <span v-if="generatedEmail" class="ec-mobile-tab-dot"></span>
            </button>
            <button
                :class="['ec-mobile-tab', mobileTab === 'context' && 'ec-mobile-tab-active']"
                @click="mobileTab = 'context'"
            >
                <i class="fa-solid fa-circle-info text-xs"></i>
                <span>Context</span>
            </button>
        </div>

        <!-- Three-panel grid (desktop) / tabbed panels (mobile) -->
        <div class="ec-panels">
            <!-- Left panel -->
            <div :class="['ec-panel-left', mobileTab !== 'compose' && 'ec-panel-hidden']">
                <record-search :selected-record="selectedRecord" @record-selected="onRecordSelected" />
                <purpose-selector
                    :selected-record="selectedRecord"
                    :generating="generating"
                    @generate="onMobileGenerate"
                />
            </div>

            <!-- Center panel -->
            <div :class="['ec-panel-center', mobileTab !== 'preview' && 'ec-panel-hidden']">
                <email-preview
                    :generated-email="generatedEmail"
                    :generating="generating"
                    :sending="sending"
                    :send-result="sendResult"
                    :edit-mode="editMode"
                    :selected-record="selectedRecord"
                    :model-name="modelName"
                    :generation-time="generationTime"
                    @send="onSend"
                    @regenerate="onGenerate({ purpose: lastPurpose, customDirection: lastDirection })"
                    @update:edit-mode="editMode = $event"
                    @update:generated-email="generatedEmail = $event"
                />
            </div>

            <!-- Right panel -->
            <div :class="['ec-panel-right', mobileTab !== 'context' && 'ec-panel-hidden']">
                <context-panel
                    :selected-record="selectedRecord"
                    :activities="activities"
                    :related="relatedRecords"
                    :loading-context="loadingContext"
                    :ai-summary="aiSummary"
                    :email-thread="emailThread"
                    :donation-summary="donationSummary"
                />
            </div>
        </div>

        <!-- Modals / Drawers -->
        <writing-profile-modal
            v-if="showProfileModal"
            :user="user"
            @close="showProfileModal = false"
            @saved="showProfileModal = false"
        />
        <email-history-drawer
            v-if="showHistory"
            :user="user"
            @close="showHistory = false"
        />
    </div>
</template>

<script>
import RecordSearch from './RecordSearch.vue'
import PurposeSelector from './PurposeSelector.vue'
import EmailPreview from './EmailPreview.vue'
import ContextPanel from './ContextPanel.vue'
import WritingProfileModal from './WritingProfileModal.vue'
import EmailHistoryDrawer from './EmailHistoryDrawer.vue'

export default {
    name: 'EmailComposer',

    components: {
        RecordSearch,
        PurposeSelector,
        EmailPreview,
        ContextPanel,
        WritingProfileModal,
        EmailHistoryDrawer,
    },

    props: {
        user: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            selectedRecord: null,
            activities: [],
            relatedRecords: {
                opportunities: [],
                account: null,
                campaigns: [],
            },
            loadingContext: false,
            generatedEmail: null,
            generating: false,
            modelName: '',
            generationTime: null,
            sending: false,
            sendResult: null,
            editMode: false,
            showProfileModal: false,
            showHistory: false,
            lastPurpose: null,
            lastDirection: null,
            aiSummary: null,
            emailThread: [],
            donationSummary: null,
            mobileTab: 'compose',
        }
    },

    methods: {
        async onRecordSelected(record) {
            this.selectedRecord = record
            this.generatedEmail = null
            this.sendResult = null
            this.emailThread = []
            this.donationSummary = null
            this.loadingContext = true

            try {
                const emailParam = record.email ? `?email=${encodeURIComponent(record.email)}` : ''
                const response = await fetch(
                    `/admin/email-composer/context/${record.type}/${record.id}${emailParam}`
                )
                const data = await response.json()
                this.activities = data.activities ?? []
                this.relatedRecords = {
                    opportunities: data.related?.opportunities ?? [],
                    account: data.related?.account ?? null,
                    campaigns: data.related?.campaigns ?? [],
                    lead_context: data.related?.lead_context ?? null,
                    contact_context: data.related?.contact_context ?? null,
                }
                this.emailThread = data.email_thread ?? []
                this.donationSummary = data.donation_summary ?? null
            } catch (err) {
                console.error('Failed to load context:', err)
            } finally {
                this.loadingContext = false
            }
        },

        onMobileGenerate(payload) {
            this.mobileTab = 'preview'
            this.onGenerate(payload)
        },

        async onGenerate({ purpose, customDirection }) {
            if (!this.selectedRecord) return

            this.lastPurpose = purpose
            this.lastDirection = customDirection
            this.generating = true
            this.generatedEmail = null
            this.sendResult = null

            try {
                const response = await fetch('/admin/email-composer/generate', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    },
                    body: JSON.stringify({
                        record_id: this.selectedRecord.id,
                        record_type: this.selectedRecord.type,
                        record_name: this.selectedRecord.name,
                        record_email: this.selectedRecord.email,
                        record_organization: this.selectedRecord.organization,
                        record_detail: this.selectedRecord.detail,
                        purpose: purpose,
                        custom_direction: customDirection,
                    }),
                })

                const reader = response.body.getReader()
                const decoder = new TextDecoder()
                let accumulatedText = ''

                while (true) {
                    const { done, value } = await reader.read()
                    if (done) break

                    const chunk = decoder.decode(value, { stream: true })
                    const lines = chunk.split('\n')

                    for (const line of lines) {
                        if (!line.startsWith('data:')) continue

                        const jsonStr = line.slice(5).trim()
                        if (jsonStr === '[DONE]') break

                        try {
                            const parsed = JSON.parse(jsonStr)
                            const delta = parsed.choices?.[0]?.delta?.content
                            if (delta) {
                                accumulatedText += delta
                                this.generatedEmail = this.parseEmail(accumulatedText)
                            }

                            if (parsed.model) {
                                this.modelName = parsed.model
                            }
                            if (parsed.usage?.generation_time) {
                                this.generationTime = parsed.usage.generation_time
                            }
                            if (parsed.ai_summary) {
                                this.aiSummary = parsed.ai_summary
                            }
                        } catch (_) {
                            // non-JSON line, skip
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to generate email:', err)
            } finally {
                this.generating = false
            }
        },

        parseEmail(text) {
            const newlineIndex = text.indexOf('\n\n')
            if (newlineIndex === -1) {
                return { subject: text.replace(/^Subject:\s*/i, ''), body: '' }
            }

            const subjectLine = text.slice(0, newlineIndex).split('\n')[0]
            const subject = subjectLine.replace(/^Subject:\s*/i, '')
            const body = text.slice(newlineIndex + 2)

            return { subject, body }
        },

        async onSend() {
            this.sending = true
            this.sendResult = null

            try {
                const response = await fetch('/admin/email-composer/send', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    },
                    body: JSON.stringify({
                        record_id: this.selectedRecord.id,
                        record_type: this.selectedRecord.type,
                        recipient_email: this.selectedRecord.email,
                        recipient_name: this.selectedRecord.name,
                        subject: this.generatedEmail.subject,
                        body: this.generatedEmail.body,
                        purpose: this.lastPurpose,
                        custom_direction: this.lastDirection,
                    }),
                })

                this.sendResult = await response.json()
            } catch (err) {
                console.error('Failed to send email:', err)
                this.sendResult = { success: false, message: 'An unexpected error occurred.' }
            } finally {
                this.sending = false
            }
        },
    },
}
</script>

<style scoped>
.ec-root {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 64px);
    background: #f9fafb;
    color: #374151;
    font-family: 'Red Hat Text', system-ui, -apple-system, sans-serif;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    margin: 8px;
}

/* ── Top bar ── */
.ec-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 52px;
    padding: 0 20px;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    border-radius: 8px 8px 0 0;
}
.ec-topbar-left {
    display: flex;
    align-items: center;
    gap: 10px;
}
.ec-brand {
    font-size: 18px;
}
.ec-brand-text {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.01em;
}
.ec-topbar-right {
    display: flex;
    align-items: center;
    gap: 8px;
}
.ec-topbar-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 12px;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    cursor: pointer;
    transition: all 0.15s ease;
}
.ec-topbar-btn:hover {
    color: #111827;
    border-color: #d1d5db;
    background: #e5e7eb;
}
.ec-topbar-btn-label {
    color: #9ca3af;
}
.ec-topbar-btn-value {
    color: #111827;
    font-weight: 600;
}
.ec-topbar-btn-icon {
    font-size: 11px;
    opacity: 0.6;
}

/* ── Mobile tab bar ── */
.ec-mobile-tabs {
    display: flex;
    border-bottom: 1px solid #e5e7eb;
    background: #fff;
    flex-shrink: 0;
}
.ec-mobile-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 0;
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    position: relative;
    transition: all 0.15s ease;
}
.ec-mobile-tab:hover {
    color: #374151;
    background: #f9fafb;
}
.ec-mobile-tab-active {
    color: #7c3aed;
    border-bottom-color: #7c3aed;
}
.ec-mobile-tab-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #10b981;
    position: absolute;
    top: 8px;
    right: calc(50% - 30px);
}

/* ── Three-panel layout ── */
.ec-panels {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

.ec-panel-left {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    background: #fff;
    flex: 1;
}

.ec-panel-center {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    background: #f9fafb;
    flex: 1;
}

.ec-panel-right {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    background: #fff;
    flex: 1;
}

.ec-panel-hidden {
    display: none;
}

/* ── Desktop: 3-column grid ── */
@media (min-width: 1024px) {
    .ec-mobile-tabs {
        display: none;
    }
    .ec-panels {
        display: grid;
        grid-template-columns: 280px 1fr 290px;
        flex-direction: row;
    }
    .ec-panel-left {
        border-right: 1px solid #e5e7eb;
    }
    .ec-panel-right {
        border-left: 1px solid #e5e7eb;
    }
    .ec-panel-hidden {
        display: flex;
    }
}

/* Scrollbar styling */
.ec-panel-left::-webkit-scrollbar,
.ec-panel-center::-webkit-scrollbar,
.ec-panel-right::-webkit-scrollbar {
    width: 4px;
}
.ec-panel-left::-webkit-scrollbar-track,
.ec-panel-center::-webkit-scrollbar-track,
.ec-panel-right::-webkit-scrollbar-track {
    background: transparent;
}
.ec-panel-left::-webkit-scrollbar-thumb,
.ec-panel-center::-webkit-scrollbar-thumb,
.ec-panel-right::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
}

/* ── Mobile utilities ── */
@media (max-width: 1023px) {
    .ec-hide-mobile {
        display: none;
    }
    .ec-topbar {
        padding: 0 12px;
        height: 44px;
    }
    .ec-brand-text {
        font-size: 13px;
    }
    .ec-root {
        margin: 0;
        border-radius: 0;
        border: none;
        height: 100vh;
    }
}
</style>
